import React from 'react';
import { Flex } from '@duxton-web/ui';
import SingleSelect from 'components/form/ucd/common/SingleSelect';
import SingleSearch from 'components/form/ucd/common/SingleSearch';
import MultiSelectDropdown from 'components/form/ucd/common/MultiSelectDropdown';
import ToggleButton from 'components/form/ucd/common/ToggleButton';

/*
TridentCampaignQuickFilters takes in the following input:
- fieldConfigs: map(field key -> fieldConfig)
- fieldValues: map(field key -> value)
- onChange: func(key, value) // the callback hook

fieldConfig is an object with the following attributes:
- name: string, the placeholder/display name of the field
- type: string, it can be "toggle", "singleSelect", "singleSearch", or "multiSelect"
- options: list of {label, value}, required if `type` is singleSelect, singleSearch or multiSelect
- allowEmpty: applicable if `type` is singleSelect, singleSearch or multiSelect, it allows a no-selection/all option
- searchPlaceholder?: string, applicable if `type` is singleSearch, custom placeholder for search mode
- width?: string, optional width for the component
- height?: string, optional height for the component

Each field will be displayed using different component depending on the `type`:
- SingleSelect for "singleSelect"
- SingleSearch for "singleSearch"
- MultiSelectDropdown for "multiSelect"
- ToggleButton for "toggle"
 */

interface Option {
  label: string;
  value: string;
}

interface FieldConfig {
  name: string;
  type: 'toggle' | 'singleSelect' | 'singleSearch' | 'multiSelect';
  options?: Option[];
  allowEmpty?: boolean;
  searchPlaceholder?: string;
  width?: string;
  height?: string;
}

interface Props {
  fieldConfigs: Record<string, FieldConfig>;
  fieldValues: Record<string, any>;
  onChange: (key: string, value: any) => void;
}

const TridentCampaignQuickFilters: React.FC<Props> = ({
  fieldConfigs,
  fieldValues,
  onChange,
}) => {


  const renderField = (key: string, config: FieldConfig) => {
    const value = fieldValues[key];
    const options = config.options || [];
    
    switch (config.type) {
      case 'singleSelect':
        return (
          <SingleSelect
            key={key}
            placeholder={config.name}
            value={value}
            options={options}
            onChange={(newValue) => onChange(key, newValue)}
            width={config.width || '120px'}
            height={config.height || '36px'}
            addNone={config.allowEmpty}
          />
        );
        
      case 'singleSearch':
        return (
          <SingleSearch
            key={key}
            placeholder={config.name}
            searchPlaceholder={config.searchPlaceholder}
            value={value}
            options={options}
            onChange={(newValue) => onChange(key, newValue)}
            width={config.width || '120px'}
            height={config.height || '36px'}
            addNone={config.allowEmpty}
          />
        );
        
      case 'multiSelect':
        return (
          <MultiSelectDropdown
            key={key}
            placeholder={config.name}
            initialValue={Array.isArray(value) ? value : (value ? [value] : [])}
            options={options}
            onChange={(newValue) => onChange(key, newValue)}
            width={config.width || '120px'}
            height={config.height || '36px'}
            multiValuePlaceholder={`Multiple ${config.name}`}
          />
        );
        
      case 'toggle':
        return (
          <ToggleButton
            key={key}
            callback={(isActive) => onChange(key, isActive)}
            title={config.name}
            initialState={false}
            checked={Boolean(value)}
            width={config.width || '140px'}
            height={config.height || '36px'}
          />
        );
        
      default:
        return null;
    }
  };

  return (
    <Flex wrap="wrap" align="center" gap="15px">
      <div style={{
            fontWeight: 600,
            fontSize: '14px',
            color: '#333',
            display: 'inline-block',
            marginRight: '10px',
          }}>
        <span>Quick Filters: </span>
      </div>

      {Object.entries(fieldConfigs).map(([key, config]) => 
        renderField(key, config)
      )}
    </Flex>
  );
};

export default TridentCampaignQuickFilters;
